import { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import { apiJson, fetcher } from '../api/client';
import type { Alert, Silence } from '../api/types';
import { StatusDot } from '../components/StatusDot';

export function AlertsPage() {
  const { data: alerts = [] } = useSWR<Alert[]>('/api/alerts/active', fetcher, {
    refreshInterval: 15_000,
  });
  const { data: silences = [] } = useSWR<Silence[]>('/api/silences', fetcher, {
    refreshInterval: 15_000,
  });
  const { mutate } = useSWRConfig();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [duration, setDuration] = useState('30m');
  const [reason, setReason] = useState('');

  const activeSilences = silences.filter(
    (s) => new Date(s.ends_at.replace(' ', 'T') + 'Z').getTime() > Date.now(),
  );

  async function silenceAlert(a: Alert) {
    if (!a.entity_id) return;
    const isServiceRule = !a.rule_id.startsWith('host-') && !a.rule_id.includes('-host');
    setBusyId(a.id);
    try {
      await apiJson('/api/silences', 'POST', {
        matcher: {
          env: a.env,
          rule_id: a.rule_id,
          ...(isServiceRule ? { service_id: a.entity_id } : { host_id: a.entity_id }),
        },
        duration,
        reason: reason || `silenced via UI for ${duration}`,
      });
      await Promise.all([mutate('/api/alerts/active'), mutate('/api/silences')]);
    } finally {
      setBusyId(null);
    }
  }

  async function endSilence(s: Silence) {
    setBusyId(s.id);
    try {
      await apiJson(`/api/silences/${s.id}`, 'DELETE');
      await Promise.all([mutate('/api/alerts/active'), mutate('/api/silences')]);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="min-h-screen bg-cockpit-bg text-cockpit-text">
      <header className="border-b border-cockpit-border bg-cockpit-bg/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-6 py-3">
          <Link to="/" className="text-cockpit-dim hover:text-cockpit-text">
            ← Dashboard
          </Link>
          <h1 className="text-lg font-semibold">Alerts</h1>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-6 py-6">
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-cockpit-dim">
            Active ({alerts.length})
          </h2>
          {alerts.length === 0 ? (
            <p className="rounded border border-cockpit-border bg-cockpit-panel p-4 text-sm text-cockpit-dim">
              no active alerts — quiet cockpit
            </p>
          ) : (
            <div className="space-y-2">
              <div className="mb-2 flex flex-wrap items-center gap-3 rounded border border-cockpit-border bg-cockpit-panel p-3 text-xs">
                <label className="flex items-center gap-2 text-cockpit-dim">
                  Silence duration
                  <input
                    type="text"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
                    placeholder="30m"
                  />
                </label>
                <label className="flex flex-1 items-center gap-2 text-cockpit-dim">
                  Reason
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
                    placeholder="optional"
                  />
                </label>
              </div>
              {alerts.map((a) => (
                <article
                  key={a.id}
                  className={`flex items-center gap-3 rounded border bg-cockpit-panel p-3 ${
                    a.severity === 'crit' ? 'border-cockpit-crit' : 'border-cockpit-warn'
                  }`}
                >
                  <StatusDot tone={a.severity} pulse={a.severity === 'crit' && !a.silenced} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold">{a.title}</span>
                      <span className="text-[10px] uppercase tracking-wider text-cockpit-dim">
                        {a.env}
                      </span>
                      {a.silenced && (
                        <span className="rounded bg-cockpit-muted px-1.5 py-0.5 text-[10px] uppercase text-cockpit-dim">
                          silenced
                        </span>
                      )}
                    </div>
                    {a.detail && <p className="text-xs text-cockpit-dim">{a.detail}</p>}
                  </div>
                  <button
                    type="button"
                    disabled={busyId === a.id || a.silenced}
                    onClick={() => silenceAlert(a)}
                    className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted disabled:opacity-40"
                  >
                    {a.silenced
                      ? 'Silenced'
                      : busyId === a.id
                        ? 'Silencing…'
                        : `Silence ${duration}`}
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-cockpit-dim">
            Active silences ({activeSilences.length})
          </h2>
          {activeSilences.length === 0 ? (
            <p className="text-sm text-cockpit-dim">none</p>
          ) : (
            <ul className="space-y-2">
              {activeSilences.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-3 rounded border border-cockpit-border bg-cockpit-panel p-3 text-xs"
                >
                  <code className="text-cockpit-text">{JSON.stringify(s.matcher)}</code>
                  <span className="text-cockpit-dim">until {s.ends_at}</span>
                  {s.reason && <span className="text-cockpit-dim">— {s.reason}</span>}
                  <button
                    type="button"
                    onClick={() => endSilence(s)}
                    disabled={busyId === s.id}
                    className="ml-auto rounded border border-cockpit-border px-3 py-1 hover:bg-cockpit-muted disabled:opacity-40"
                  >
                    {busyId === s.id ? '…' : 'End'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
